'use client';

import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { useReduxUser } from '@hooks/common/useAuthClient/useReduxUser';

const MC_CONTEXT_KEY = 'mc_context';
const MC_CONTEXT_EVENT = 'mc-context-change';

export type MissionControlContext = 'candidate' | 'company';

interface UseMissionControlContextOptions {
  /**
   * Server-derived initial context (from session token on first render).
   * Prevents the candidate→company flash for company users.
   * Only used when there is no explicit localStorage preference.
   */
  initialContext?: MissionControlContext;
}

interface UseMissionControlContextReturn {
  currentContext: MissionControlContext;
  switchContext: () => void;
  clearContext: () => void;
}

/**
 * @description Hook for managing Mission Control context (candidate vs company view).
 *
 * Priority order:
 * 1. Explicit localStorage preference (user has manually switched at least once).
 * 2. `initialContext` prop (server-derived from session token — no flash, no extra fetch).
 * 3. Live `headerInfo.hasBusinessWallet` from Redux (catches late-loading headerInfo).
 * 4. Fallback: 'candidate'.
 *
 * localStorage is only written when the user explicitly calls switchContext(),
 * preventing the race condition where the auto-derived 'candidate' default is
 * persisted before headerInfo finishes loading.
 */
export const useMissionControlContext = ({ initialContext }: UseMissionControlContextOptions = {}): UseMissionControlContextReturn => {
  const { headerInfo } = useReduxUser();
  const isCompany = !!(headerInfo as ApiSchema<'HeaderInfo2'> | undefined)?.hasBusinessWallet;

  // Track whether the user has explicitly chosen a context (stored in localStorage).
  // Only when this is true do we persist changes and respect the stored value.
  const hasExplicitPreferenceRef = useRef(false);

  const [currentContext, setCurrentContext] = useState<MissionControlContext>(() => {
    if (typeof window === 'undefined') return initialContext ?? 'candidate';
    try {
      const stored = localStorage.getItem(MC_CONTEXT_KEY);
      if (stored === 'candidate' || stored === 'company') {
        hasExplicitPreferenceRef.current = true;
        return stored;
      }
    } catch {
      // ignore
    }
    // No explicit preference — use server hint or Redux-derived value.
    return initialContext ?? (isCompany ? 'company' : 'candidate');
  });

  // If headerInfo arrives after mount and no explicit preference has been set,
  // auto-switch to company context. Does NOT write to localStorage.
  useEffect(() => {
    if (hasExplicitPreferenceRef.current) return;
    if (!isCompany) return;
    setCurrentContext('company');
  }, [isCompany]);

  // Listen for context changes dispatched by other hook instances (e.g. header vs MissionControl).
  useEffect(() => {
    const handleExternalSwitch = (e: Event) => {
      const next = (e as CustomEvent<MissionControlContext>).detail;
      if (next === 'candidate' || next === 'company') {
        setCurrentContext(next);
      }
    };

    window.addEventListener(MC_CONTEXT_EVENT, handleExternalSwitch);
    return () => window.removeEventListener(MC_CONTEXT_EVENT, handleExternalSwitch);
  }, []);

  const switchContext = useCallback(() => {
    setCurrentContext((prev) => {
      const next: MissionControlContext = prev === 'candidate' ? 'company' : 'candidate';

      // Mark as explicit and persist.
      hasExplicitPreferenceRef.current = true;
      try {
        localStorage.setItem(MC_CONTEXT_KEY, next);
      } catch (error) {
        console.warn('Failed to save mission control context to localStorage:', error);
      }

      setTimeout(() => {
        window.dispatchEvent(new CustomEvent<MissionControlContext>(MC_CONTEXT_EVENT, { detail: next }));
      }, 0);
      return next;
    });
  }, []);

  const clearContext = useCallback(() => {
    hasExplicitPreferenceRef.current = false;
    setCurrentContext('candidate');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent<MissionControlContext>(MC_CONTEXT_EVENT, { detail: 'candidate' }));
    }, 0);

    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(MC_CONTEXT_KEY);
    } catch (error) {
      console.warn('Failed to clear mission control context from localStorage:', error);
    }
  }, []);

  return {
    currentContext,
    switchContext,
    clearContext,
  };
};
